/**
 * VersionUpdateBanner — surfaces the version-skew refresh prompt.
 *
 * Theme 4. Renders nothing of its own; it drives a persistent (non-auto-dismiss)
 * sonner toast via the already-mounted <Toaster> in main.tsx. When a newer build
 * is detected the user sees:
 *
 *   "A new version of Keystone is available — Refresh"
 *
 * The Refresh action does a guarded hard reload (see useVersionCheck.refreshNow:
 * it confirms first if there is dirty/in-progress state). The toast does NOT
 * auto-dismiss and cannot be permanently dismissed into silence — version skew
 * is a correctness issue (stale tab re-exposes fixed bugs), so we keep nudging.
 *
 * Mounted at the app-shell level in App.tsx (NOT main.tsx — main.tsx is owned by
 * a parallel reliability build; touching it would create a merge conflict).
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useVersionCheck } from '@/hooks/useVersionCheck';

const TOAST_ID = 'keystone-version-update';

export function VersionUpdateBanner() {
  const { updateAvailable, refreshNow } = useVersionCheck();
  const shownRef = useRef(false);

  useEffect(() => {
    if (!updateAvailable || shownRef.current) return;
    shownRef.current = true;

    toast('A new version of Keystone is available', {
      id: TOAST_ID,
      description: 'Refresh to load the latest version.',
      duration: Infinity, // persistent — never auto-dismiss
      dismissible: false, // can't be swiped away; refresh is the only resolution
      action: {
        label: 'Refresh',
        onClick: () => {
          refreshNow();
        },
      },
    });
  }, [updateAvailable, refreshNow]);

  return null;
}

export default VersionUpdateBanner;
